import {
  Choice,
  DateTimePicker,
  DateTimePickerProps,
  Separator,
  TimePicker,
  TimePickerProps,
  TimeType,
} from 'platform/components';
import {Grid, Heading, Text, VStack} from 'platform/foundation';
import {LocaleProvider, useLocale} from 'platform/locale';

import {useState} from 'react';

import {isNotNil} from 'ramda';

// The three timeShort formats offered by Settings → Locale — mirrors
// libs/dms/modules/settings/settings-core/src/pages/Locale/constants/localeOptions.ts
// (not importable here: it is not part of the settings-core public API).
const TIME_FORMAT_OPTIONS = [
  {value: 'H:mm', label: '9:30 (H:mm)'},
  {value: 'h:mm aa', label: '9:30 AM (h:mm aa)'},
  {value: 'HH:mm', label: '09:30 (HH:mm)'},
];

const CONFIGURED_RELATIVE_TIMES: TimePickerProps['relativeTimes'] = [
  'currentTime',
  {time: '07:00'},
  {time: '12:00'},
  {time: '15:00'},
  {time: '17:00'},
];

interface ShowcaseTimePickerProps {
  caption: string;
  initialValue?: TimeType | null;
  timePickerProps?: Omit<TimePickerProps, 'value' | 'onChange'>;
}

function ShowcaseTimePicker(props: ShowcaseTimePickerProps) {
  const [value, setValue] = useState<TimeType | null>(props.initialValue ?? null);

  return (
    <VStack spacing={1}>
      <Text size="xSmall" color="tertiary">
        {props.caption}
      </Text>
      <TimePicker value={value} onChange={setValue} {...props.timePickerProps} />
    </VStack>
  );
}

interface ShowcaseDateTimePickerProps {
  caption: string;
  dateTimePickerProps?: Omit<DateTimePickerProps, 'value' | 'onChange'>;
}

function ShowcaseDateTimePicker(props: ShowcaseDateTimePickerProps) {
  const [value, setValue] = useState<Date | null>(null);

  return (
    <VStack spacing={1}>
      <Text size="xSmall" color="tertiary">
        {props.caption}
      </Text>
      <DateTimePicker value={value} onChange={setValue} {...props.dateTimePickerProps} />
    </VStack>
  );
}

export function TimePickerShowcaseContent() {
  const {language, localeConfig} = useLocale();
  const [timeFormat, setTimeFormat] = useState<string>(localeConfig.dateTimeFormat.timeShort);

  const handleTimeFormatChange = (value: string | null) => {
    if (isNotNil(value)) {
      setTimeFormat(value);
    }
  };

  const showcaseLocaleConfig = {
    ...localeConfig,
    dateTimeFormat: {...localeConfig.dateTimeFormat, timeShort: timeFormat},
  };

  return (
    <VStack spacing={4}>
      <Grid columns={4} spacing={4}>
        <Choice<string>
          label="Time format (timeShort — same options as Settings → Locale)"
          value={timeFormat}
          onChange={handleTimeFormatChange}
          options={TIME_FORMAT_OPTIONS}
          data-testid="devSettings-timePickerShowcase-timeFormat"
        />
      </Grid>

      {/* The key remounts every picker on format change, resetting their mocked state. */}
      <LocaleProvider key={timeFormat} language={language} config={showcaseLocaleConfig}>
        <Heading size={4}>TimePicker — redesigned behavior</Heading>
        <Text size="xSmall" color="tertiary">
          Every picker is interactive. Try typing too — 1530, 15.30, 930 or 9 all resolve; an
          off-interval time like 15:07 stays reachable even though the list only offers rounded
          times.
        </Text>
        <Grid columns={4} spacing={4}>
          <ShowcaseTimePicker caption="Default (empty — opens on the next rounded time)" />
          <ShowcaseTimePicker caption="With value (opens on the value)" initialValue="14:30" />
          <ShowcaseTimePicker
            caption="Now chip only"
            timePickerProps={{relativeTimes: ['currentTime']}}
          />
          <ShowcaseTimePicker
            caption="Configured chips (07/12/15/17 + now)"
            timePickerProps={{relativeTimes: CONFIGURED_RELATIVE_TIMES}}
          />
          <ShowcaseTimePicker
            caption="Custom chip labels"
            timePickerProps={{
              relativeTimes: [
                {time: '08:00', label: 'Opening'},
                {time: '17:00', label: 'Closing'},
              ],
            }}
          />
          <ShowcaseTimePicker
            caption="Out-of-range chip hidden (max 16:00 — 17:00 must not appear)"
            timePickerProps={{maxTime: '16:00', relativeTimes: [{time: '12:00'}, {time: '17:00'}]}}
          />
        </Grid>

        <Separator />

        <Heading size={4}>DateTimePicker</Heading>
        <Grid columns={2} spacing={4}>
          <ShowcaseDateTimePicker caption="Default" />
          <ShowcaseDateTimePicker
            caption="With relativeTimes"
            dateTimePickerProps={{relativeTimes: ['currentTime', {time: '08:00'}]}}
          />
        </Grid>
      </LocaleProvider>
    </VStack>
  );
}
